import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { EXAM_CONFIGURATION_FIELD_MODE } from 'components/AppService/ExamInCoreConfiguration/helpers';
import { EXAM_CONFIGURATION_CONFIGS } from 'constants/Exam.constants';
import _ from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IOption } from 'types/option.interface';
import type { IDefaultReducerState } from 'types/state.interface';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';

export type TExamInCoreSettingsState = IDefaultReducerState &
  TExamInCoreSettings & {
    configureFields: { [index: string]: IOption[] };
  };

export type TConfiguredField = {
  source: string | null;
  mode: string | null;
  field: string | null;
};

export type TExamInCoreSettings = {
  examInCore: TExamInCore;
};

export type TExamInCore = {
  isEnabled: boolean;
  configureFields?: {
    [type: string]: TConfiguredField;
  };
};

export const initialState: TExamInCoreSettingsState = {
  loading: false,
  hasErrors: false,
  examInCore: {
    isEnabled: false,
    configureFields: {},
  },
  configureFields: {
    // initialize all fields for exam in core with empty array
    ...EXAM_CONFIGURATION_CONFIGS.reduce(
      (acc, field) => ({
        ...acc,
        [field.config]: [],
      }),
      {}
    ),
  },
};

export type TConfigureFieldLocal = {
  config: string;
  type: string;
  value: string;
};

// Slice
const slice = createSlice({
  name: 'examInCoreSettings',
  initialState,
  reducers: {
    fetchExamInCoreSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchExamInCoreSettingsSuccess: (
      state,
      { payload }: PayloadAction<TExamInCore>
    ) => {
      state.examInCore = payload;
      finishedLoadingSuccess(state);
    },
    fetchExamInCoreSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    saveExamInCoreSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    saveExamInCoreSettingsSuccess: (state) => {
      finishedLoadingSuccess(state);
    },
    saveExamInCoreSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    fetchConfigFieldsSuccess: (
      state,
      { payload }: PayloadAction<{ result?: string[]; config: string }>
    ) => {
      const { result, config } = payload;
      if (!result || !result.length) return;

      state.configureFields[config] = result.map((field: string) => ({
        label: field,
        value: field,
      }));
      finishedLoadingSuccess(state);
    },
    updateConfigureFieldLocal: (
      state,
      { payload: { config, type, value } }: PayloadAction<TConfigureFieldLocal>
    ) => {
      const fieldPath = `examInCore.configureFields.${config}`;

      // reset the field if the source or mode is changed
      switch (type) {
        case 'source':
          _.set(state, `${fieldPath}.mode`, null);
          _.set(state, `${fieldPath}.field`, null);
          _.set(state, `configureFields.${config}`, []); // clear the options for the field as well
          break;
        case 'mode':
          _.set(state, `${fieldPath}.field`, null);
          break;
      }

      _.set(state, `${fieldPath}.${type}`, value);
    },
    updateEnabledLocal: (state, { payload }: PayloadAction<boolean>) => {
      _.set(state, `examInCore.isEnabled`, payload);
    },
  },
});

export default slice.reducer;

// Selectors
export const examInCoreSettingsSelector = (state: TRootState) =>
  state.examInCoreSettings;
export const configFieldOptionsSelector =
  (config: string) => (state: TRootState) =>
    state.examInCoreSettings.configureFields[config] || [];
export const configFieldSelector = (config: string) => (state: TRootState) =>
  state.examInCoreSettings.examInCore.configureFields?.[config] || {
    source: null,
    mode: null,
    field: null,
  };
export const examInCoreLoading = (state: TRootState) =>
  state.examInCoreSettings.loading;

// Actions
export const {
  fetchExamInCoreSettingsRequest,
  fetchExamInCoreSettingsSuccess,
  fetchExamInCoreSettingsFailure,
  saveExamInCoreSettingsRequest,
  saveExamInCoreSettingsSuccess,
  saveExamInCoreSettingsFailure,
  fetchConfigFieldsSuccess,
  updateConfigureFieldLocal,
  updateEnabledLocal,
} = slice.actions;

export const fetchExamInCoreSettings = createAppAsyncThunk(
  'examInCoreSettings/fetchExamInCoreSettings',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchExamInCoreSettingsRequest());
      const result = (await api.get({
        endpoint: `/apps/TE_EXAM/exam-in-core`,
      })) as TExamInCore;
      dispatch(fetchExamInCoreSettingsSuccess(result));
    } catch (e) {
      dispatch(fetchExamInCoreSettingsFailure());
      console.error(e);
    }
  }
);

export const fetchObjectFields = createAppAsyncThunk<
  string[] | undefined,
  string
>('examInCoreSettings/fetchObjectFields', async (object, { dispatch }) => {
  try {
    const result = await api.get({
      endpoint: `/apps/TE_EXAM/integration/objects/types/${object}/fields`,
    });
    return result.data || [];
  } catch (e) {
    dispatch(fetchExamInCoreSettingsFailure());
    console.error(e);
  }
});

export const fetchReservationFields = createAppAsyncThunk<
  string[] | undefined,
  string
>('examInCoreSettings/fetchReservationFields', async (object, { dispatch }) => {
  try {
    const result = await api.get({
      endpoint: `/apps/TE_EXAM/integration/reservations/fields/${object}`,
    });
    return result.data || [];
  } catch (e) {
    dispatch(fetchExamInCoreSettingsFailure());
    console.error(e);
  }
});

export const fetchConfigFields = createAppAsyncThunk<
  void,
  { config: string; object: string | null }
>(
  'examInCoreSettings/fetchConfigFields',
  async ({ config, object }, { dispatch, getState }) => {
    const source =
      getState().examInCoreSettings.examInCore?.configureFields?.[config]
        ?.source;
    if (!source || !object) return;
    const result = await dispatch(
      source === EXAM_CONFIGURATION_FIELD_MODE.OBJECT_TYPES
        ? fetchObjectFields(object)
        : fetchReservationFields(object)
    ).unwrap();
    dispatch(fetchConfigFieldsSuccess({ config, result }));
  }
);

export const saveExamInCoreSettings = createAppAsyncThunk<void, TExamInCore>(
  'examInCoreSettings/saveExamInCoreSettings',
  async (examInCoreSettings, { dispatch }) => {
    try {
      dispatch(saveExamInCoreSettingsRequest());
      const payload = _.omit(examInCoreSettings, [
        'id',
        'organizationId',
        'createdAt',
        'updatedAt',
        'configureFields.examTitle._id',
        'configureFields.student._id',
        'configureFields.externalRef._id',
        'configureFields.adminInfo._id',
        'configureFields.studentInfo._id',
        'configureFields.supervisorInfo._id',
        'orgIdV2',
      ]);
      await api.patch({
        endpoint: '/apps/TE_EXAM/exam-in-core',
        data: payload,
      });
      dispatch(saveExamInCoreSettingsSuccess());
    } catch (error) {
      dispatch(saveExamInCoreSettingsFailure());
      console.error(error);
    }
  }
);
